/**
 * StatePreservationPlugin
 *
 * Provides functionality for preserving and restoring store target state
 * when navigating between browse/list pages and detail pages.
 *
 * Usage:
 * 1. Call $this.StatePreservation_preserveStateOnMount() in onMounted() hook
 *
 */

export default {
  install(app) {

    /**
     * Determines if current state should be preserved based on navigation context
     * @param {Array} routesToPreserve - Array of route names that should preserve state
     * @returns {boolean} true if state should be preserved
     */
    function shouldPreserve(routesToPreserve = []) {
      // Don't preserve state if navigating from sidebar (fresh start)
      if (window.history.state.fromSidebar) {
        return false
      }

      // Preserve state when returning from specified routes (usually detail pages)
      const previousRouteName = $this.$store.state.App?.previousRouteInfo?.name
      return routesToPreserve.includes(previousRouteName)
    }

    /**
     * Handles state preservation logic during component mounting
     * @param {string} storeNamespace - The Vuex store namespace
     * @param {Array} routesToPreserve - Array of route names that should preserve state
     * @param {Object} defaultState - Default state structure
     * @param {Function} initCallback - Optional callback to initialize the store
     * @returns {Object} The state to be restored
     */
    async function preserveStateOnMount(
      storeNamespace,
      routesToPreserve = [],
      defaultState = {},
      initCallback = null
    ) {
      try {
        // Start with default state
        let persistedState = { ...defaultState }

        // Determine if we should preserve state based on navigation source
        const shouldPreserveState = shouldPreserve(routesToPreserve)

        if (shouldPreserveState && $this.$store.state[storeNamespace]) {
          // Preserve state only for keys defined in defaultState
          const stateToPreserve = {}
          Object.keys(defaultState).forEach(key => {
            if (Object.prototype.hasOwnProperty.call($this.$store.state[storeNamespace], key)) {
              stateToPreserve[key] = JSON.parse(JSON.stringify($this.$store.state[storeNamespace][key]))
            }
          })
          persistedState = { ...defaultState, ...stateToPreserve }
        }

        try {
          // Reset store state and initialize
          $this.$resetStoreState(storeNamespace)
        } catch (error) {
          console.warn(`Failed to reset store for ${storeNamespace}:`, error)
        }

        // Initialize the store via callback if provided
        if (initCallback && typeof initCallback === 'function') {
          await initCallback()
        }

        // Restore preserved state
        if ($this.$store.state[storeNamespace] && persistedState) {
          $this.$setState(storeNamespace, persistedState)
        }

        return persistedState
      } catch (error) {
        console.error(`Failed to preserve state for ${storeNamespace}:`, error)
        throw error
      }
    }

    app.config.globalProperties.$StatePreservation_preserveStateOnMount = preserveStateOnMount
  }
}
